<script lang="ts">
export default {
  name: 'ProgramGroupingRow',
};
</script>

<script setup lang="ts">
import { computed } from 'vue';
import { MinusIcon, PencilIcon, PlusIcon } from '@lucide/vue';
import AccountProgramContact from '@/components/AccountProgramContact.vue';
import type { Program } from '@/types/Programs';
import type { ProgramContact } from '@/types/AccountContact';
import type { InactiveServicer } from '@/services/v2/ClientReadinessService';

const props = withDefaults(
  defineProps<{
    program: Program;
    accountId: number;
    servicingRoleMissing: boolean;
    programContact: ProgramContact | null;
    isOpen: boolean;
    inactiveServicers?: InactiveServicer[];
  }>(),
  { inactiveServicers: () => [] },
);

defineEmits<{
  toggle: [];
  edit: [];
}>();

// Matches AccountProgramForm's rule: Automated and EPIC programs are generated, not authored.
const isEditable = computed(() => props.program.type !== 'Automated' && props.program.type !== 'EPIC');
</script>

<template>
  <div class="border-b border-base-300 last:border-b-0">
    <!-- The toggle is its own button rather than wrapping the row, so the Edit action beside
         it isn't a nested interactive element. -->
    <div
      class="flex w-full items-center gap-3 px-4 py-3 transition-colors hover:bg-base-200/50"
      :class="{ 'bg-base-200/50': isOpen }">
      <button
        class="flex min-w-0 flex-1 items-center gap-3 text-left"
        data-testid="program-row-toggle"
        :aria-expanded="isOpen"
        @click="$emit('toggle')">
        <MinusIcon
          v-if="isOpen"
          :size="14"
          class="shrink-0 stroke-secondary" />
        <PlusIcon
          v-else
          :size="14"
          class="shrink-0 stroke-secondary" />

        <div class="min-w-0 flex-1">
          <router-link
            :to="`/account/${accountId}/programs/${program.id}`"
            class="link link-primary text-sm font-semibold"
            @click.stop>
            {{ program.name }}
          </router-link>
          <div class="text-xs text-base-content/60">
            {{ program.type }} · {{ program.year }} · {{ program.policyCount }} polic{{ program.policyCount === 1 ? 'y' : 'ies' }}
          </div>
        </div>
      </button>

      <span
        data-testid="servicing-role-badge"
        class="badge shrink-0 badge-sm"
        :class="servicingRoleMissing ? 'badge-error' : 'badge-success'">
        {{ servicingRoleMissing ? 'Servicing Roles Missing' : 'Servicing Roles Assigned' }}
      </span>

      <span
        v-if="inactiveServicers.length > 0"
        data-testid="inactive-servicer-badge"
        class="badge shrink-0 badge-outline badge-sm badge-error">
        Inactive Servicer
      </span>

      <button
        v-if="isEditable"
        class="btn shrink-0 gap-1 btn-ghost btn-xs"
        data-testid="program-edit"
        :aria-label="`Edit ${program.name}`"
        @click="$emit('edit')">
        <PencilIcon :size="12" />
        Edit
      </button>
    </div>

    <div
      v-if="isOpen"
      class="border-t border-base-300 bg-base-200 px-4 py-4">
      <div
        v-if="inactiveServicers.length > 0"
        data-testid="inactive-servicers"
        class="mb-3 text-sm">
        <div class="font-semibold text-error">Inactive servicers</div>
        <div
          v-for="servicer in inactiveServicers"
          :key="`${servicer.name}-${servicer.servicingRoleTitle}`"
          class="text-base-content/80">
          {{ servicer.name }} ({{ servicer.servicingRoleTitle }}) — inactive in Epic<template v-if="servicer.inactiveReason">: {{ servicer.inactiveReason }}</template>
        </div>
      </div>
      <div
        v-if="programContact"
        class="text-xs">
        <AccountProgramContact
          :program-contact="programContact"
          :expand="true" />
      </div>
      <div
        v-else-if="servicingRoleMissing"
        data-testid="no-servicing-role"
        class="flex items-center gap-2 text-sm text-error">
        <span class="font-semibold">Missing servicing role</span>
        <span class="text-base-content/60">— assign this program a servicing role via the Program Details page.</span>
      </div>
      <div
        v-else
        data-testid="no-contact-details"
        class="text-sm text-base-content/60">
        No contact details available for this program.
      </div>
    </div>
  </div>
</template>
